const dedupeWindowMs = 1500;
const allowedEventParamKeys = new Set([
  'form_id',
  'form_name',
  'form_location',
  'lead_type',
  'page_location',
  'page_title',
  'source_component',
  'value',
  'currency',
  'plan_name',
  'billing_period',
  'displayed_price',
  'cta_location',
  'competitor',
  'cta_text',
  'file_name',
  'file_extension',
]);
const recentEventFingerprints = new Map();

let hasInitializedAnalytics = false;

const canUseWindow = typeof window !== 'undefined';

const pruneRecentEventFingerprints = (now) => {
  for (const [fingerprint, timestamp] of recentEventFingerprints.entries()) {
    if (now - timestamp > dedupeWindowMs) {
      recentEventFingerprints.delete(fingerprint);
    }
  }
};

const sanitizeEventParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) =>
        allowedEventParamKeys.has(key) &&
        value !== undefined &&
        value !== null &&
        value !== '',
    ),
  );

const getEventFingerprint = (eventName, params) =>
  [
    eventName,
    params.form_id || '',
    params.form_location || '',
    params.page_location || '',
    params.lead_type || '',
    params.source_component || '',
    params.plan_name || '',
    params.billing_period || '',
    params.cta_location || '',
    params.competitor || '',
    params.cta_text || '',
  ].join('|');

const shouldSkipDuplicateEvent = (eventName, params) => {
  const now = Date.now();
  const fingerprint = getEventFingerprint(eventName, params);
  const lastSeenAt = recentEventFingerprints.get(fingerprint);

  pruneRecentEventFingerprints(now);

  if (lastSeenAt && now - lastSeenAt < dedupeWindowMs) {
    return true;
  }

  recentEventFingerprints.set(fingerprint, now);
  return false;
};

export const initializeAnalytics = () => {
  if (hasInitializedAnalytics || !canUseWindow) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  // GTM loads the Google tag but has no custom-event forwarding tags.
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  hasInitializedAnalytics = true;
};

export const trackGa4Event = (eventName, params = {}) => {
  if (!canUseWindow || !eventName) {
    return false;
  }

  const sanitizedParams = sanitizeEventParams(params);

  if (shouldSkipDuplicateEvent(eventName, sanitizedParams)) {
    return false;
  }

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, { ...sanitizedParams, send_to: 'G-Y96SXEH1D2' });
      return true;
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...sanitizedParams,
      });
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

export const trackLeadFormSubmission = ({
  formContext,
  includeGenerateLead = false,
  includeSignUp = false,
  includeCloseConvertLead = false,
  includeQualifyLead = false,
} = {}) => {
  if (!formContext) {
    return;
  }

  trackGa4Event('form_submit', formContext);
  if (includeSignUp) trackGa4Event('sign_up', formContext);

  if (includeSignUp) trackGa4Event('sign_up', formContext);

  if (includeGenerateLead) {
    trackGa4Event('generate_lead', formContext);
  }

  if (includeCloseConvertLead) {
    trackGa4Event('close_convert_lead', formContext);
  }

  if (includeQualifyLead) {
    trackGa4Event('qualify_lead', formContext);
  }
};
